import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { SegmentExport, TSegmentExportType } from '@feathr/blackbox';
import type { ITanStackColumnMeta } from '@feathr/components';
import { Text, Time } from '@feathr/components';
import { formatBytes, TimeFormat } from '@feathr/hooks';

import ExportProgressCell from './ExportProgressCell';
import ExportStateChip from './ExportStateChip';
import optionsColumn from './optionsColumn';

const exportTypeLabel: Record<TSegmentExportType, string> = {
  email_activity: 'Email',
  persons: 'Person',
  breadcrumb: 'Other',
};

/**
 * Column definitions for the `/data/exports` dashboard table.
 *
 * No column declares a `size`. `TanStackTable` measures each column against its own content and
 * overwrites `size`, `minSize`, and `maxSize` with what it measured, so a width set here never
 * reaches the screen.
 *
 * Each column `id` is the field name `PersonSegmentExport` stores, because
 * `SegmentExportListView.get` hands the requested sort straight to `order_by`. A column that
 * renames its id stops sorting rather than erroring.
 *
 * Every cell body reads the model through `Observer`. `TanStackTable` invokes these callbacks
 * inside its own body, so a `get()` here is not tracked by the page's `observer` and the cell
 * would never re-render.
 *
 * @param columnIds - When non-empty, only columns whose `id` is in the list will render. Empty
 *   array returns all columns.
 */
export default function dataExportsColumns(
  columnIds: string[],
  t: TFunction,
): Array<ColumnDef<SegmentExport>> {
  const columns: Array<ColumnDef<SegmentExport>> = [
    {
      accessorFn: (row): string => row.name,
      cell: ({ row }: CellContext<SegmentExport, unknown>): JSX.Element => (
        <Observer>
          {(): JSX.Element => (
            <Text.DefaultHeadingMedium inline>{row.original.name || '—'}</Text.DefaultHeadingMedium>
          )}
        </Observer>
      ),
      header: t('Name'),
      id: 'name',
      /*
       * Name, Status, and Progress split the width the other columns do not use, so the table
       * fills its container instead of leaving a gap on the right. Three columns rather than one
       * because the split is even: handing the whole surplus to Name alone left it 71% of the
       * table, with whitespace after the text and the data columns crowded against the edge.
       *
       * `isPrimary` keeps the 150-350px auto-width range on the column that identifies the row,
       * which is this one. It is deliberately separate from `isFlex`.
       */
      meta: { isFlex: true, isPrimary: true } satisfies ITanStackColumnMeta,
      // Ascending on the first header click; TanStack would otherwise start descending.
      sortDescFirst: false,
    },
    {
      // The sort field is the column id. This value only satisfies TanStack's rule that a sortable
      // column must have an accessor, since sorting is server-side.
      accessorFn: (row): string => row.get('state'),
      cell: ({ row }: CellContext<SegmentExport, unknown>): JSX.Element => (
        <ExportStateChip segmentExport={row.original} />
      ),
      header: t('Status'),
      id: 'state',
      meta: { align: 'center', isFlex: true } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    {
      /*
       * The one column the list route cannot sort: anhinga derives `progress` from
       * `persons_written` / `rows_estimated` on read, so there is no stored field for `order_by`
       * to reach. `enableSorting: false` is what removes the glyph — `TanStackTable` renders it
       * from the column's own sortability, so a header can no longer promise a sort it lacks.
       */
      cell: ({ row }: CellContext<SegmentExport, unknown>): JSX.Element => (
        <ExportProgressCell segmentExport={row.original} />
      ),
      enableSorting: false,
      header: t('Progress'),
      id: 'progress',
      meta: { isFlex: true } satisfies ITanStackColumnMeta,
    },
    {
      accessorFn: (row): string => row.get('export_type'),
      cell: ({ row }: CellContext<SegmentExport, unknown>): JSX.Element => (
        <Observer>
          {(): JSX.Element => <>{t(exportTypeLabel[row.original.get('export_type')])}</>}
        </Observer>
      ),
      header: t('Type'),
      id: 'export_type',
      meta: { align: 'center' } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    {
      accessorFn: (row): string => row.dateCreated.format(),
      cell: ({ row }: CellContext<SegmentExport, unknown>): JSX.Element => (
        <Observer>
          {(): JSX.Element => (
            <Time format={TimeFormat.shortDate} timestamp={row.original.dateCreated.format()} />
          )}
        </Observer>
      ),
      header: t('Created'),
      id: 'date_created',
      meta: { align: 'center' } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    {
      accessorFn: (row): string => row.dateFinished?.format() ?? '',
      cell: ({ row }: CellContext<SegmentExport, unknown>): JSX.Element => (
        <Observer>
          {(): JSX.Element => {
            const finished = row.original.dateFinished;
            if (!finished) {
              return <>—</>;
            }
            return <Time format={TimeFormat.shortDate} timestamp={finished.format()} />;
          }}
        </Observer>
      ),
      header: t('Date exported'),
      id: 'date_finished',
      meta: { align: 'center' } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    {
      accessorFn: (row): number => row.get('file_size') ?? 0,
      cell: ({ row }: CellContext<SegmentExport, unknown>): JSX.Element => (
        <Observer>
          {(): JSX.Element => {
            const fileSize = row.original.get('file_size');
            if (!row.original.dateFinished || fileSize === null || fileSize === undefined) {
              return <>—</>;
            }
            return <>{formatBytes(fileSize)}</>;
          }}
        </Observer>
      ),
      header: t('File size'),
      id: 'file_size',
      meta: { align: 'right' } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    optionsColumn(t),
  ];

  return columnIds.length
    ? columns.filter((column) => column.id !== undefined && columnIds.includes(column.id))
    : columns;
}
